type ErrorMap = Record<string, unknown>;

export type FieldErrorSource = 'all' | 'external' | 'field' | 'form';

export type FieldErrorContext = {
  readonly errors: readonly string[];
  readonly externalErrors: readonly string[];
  readonly fieldErrors: readonly string[];
  readonly fieldName: string;
  readonly formErrors: readonly string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function getMessages(error: unknown): string[] {
  if (typeof error === 'string') {
    return error ? [error] : [];
  }

  if (Array.isArray(error)) {
    return error.flatMap(getMessages);
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return error.message ? [error.message] : [];
  }

  return [];
}

export function normalizeFieldErrors(errors: readonly unknown[]): string[] {
  return [...new Set(errors.flatMap(getMessages))];
}

export function getErrorsByValidationSource({
  errorMap,
  errorSourceMap,
  source,
}: {
  errorMap: ErrorMap;
  errorSourceMap: Record<string, unknown>;
  source: 'field' | 'form';
}): string[] {
  return normalizeFieldErrors(
    Object.entries(errorMap).flatMap(([key, error]) =>
      errorSourceMap[key] === source ? [error] : [],
    ),
  );
}

export function getPathMatchedFormErrors(
  errorMap: ErrorMap,
  fieldName: string,
): string[] {
  return normalizeFieldErrors(
    Object.values(errorMap).flatMap((error) => {
      if (!isRecord(error) || !isRecord(error.fields)) {
        return [];
      }

      return [error.fields[fieldName]];
    }),
  );
}

export function mergeFieldErrors(
  ...errorGroups: ReadonlyArray<readonly string[]>
): string[] {
  return [...new Set(errorGroups.flat())];
}
